// AnalyticsTab — Win/loss charts, winning distance, spread comparison,
// volume/P&L, settlement reliability, regulatory extract (CSV export).

import React, { useState, useCallback } from 'react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import { Download, Activity } from 'lucide-react';

const CHART_COLORS = ['#52525b', '#a1a1aa', '#d4d4d8', '#3832e3', '#7b7eff'];

const formatCurrency = (val) => {
  if (val >= 1000000) return `$${(val / 1000000).toFixed(1)}M`;
  if (val >= 1000) return `$${(val / 1000).toFixed(0)}K`;
  return `$${val}`;
};

const AnalyticsTab = ({ quotes = [], trades = [], competitiveContext, onExportRegulatory }) => {
  const [exporting, setExporting] = useState(false);

  // Win/Loss data from quotes
  const wonQuotes = quotes.filter(q => q.status === 'accepted');
  const lostQuotes = quotes.filter(q => q.status === 'rejected');
  const expiredQuotes = quotes.filter(q => q.status === 'expired');
  const withdrawnQuotes = quotes.filter(q => q.status === 'withdrawn');

  const quoteBreakdown = [
    { name: 'Won', value: wonQuotes.length },
    { name: 'Lost', value: lostQuotes.length },
    { name: 'Expired', value: expiredQuotes.length },
    { name: 'Withdrawn', value: withdrawnQuotes.length },
  ].filter(d => d.value > 0);

  // Volume by pair
  const volumeByPair = {};
  trades.forEach(t => {
    volumeByPair[t.currencyPair] = (volumeByPair[t.currencyPair] || 0) + t.amount;
  });
  const pairVolume = Object.entries(volumeByPair)
    .map(([pair, volume]) => ({ pair, volume }))
    .sort((a, b) => b.volume - a.volume);

  // Spread data from quotes
  const spreads = quotes
    .filter(q => q.midRate && q.clientRate)
    .map(q => ({
      id: q.id,
      pair: q.currencyPair,
      spread: Math.abs(q.clientRate - q.midRate),
      marginPct: q.midRate > 0 ? (Math.abs(q.clientRate - q.midRate) / q.midRate) * 100 : 0,
      status: q.status,
    }));

  const avgWinSpread = wonQuotes.length > 0
    ? wonQuotes.reduce((s, q) => s + Math.abs(q.clientRate - q.midRate), 0) / wonQuotes.length
    : 0;
  const avgLossSpread = lostQuotes.length > 0
    ? lostQuotes.reduce((s, q) => s + Math.abs(q.clientRate - q.midRate), 0) / lostQuotes.length
    : 0;

  // Settlement reliability
  const settledTrades = trades.filter(t => ['settled', 'csd_payment_confirmed'].includes(t.status));
  const failedTrades = trades.filter(t => t.status === 'failed');
  const settlementRate = trades.length > 0
    ? ((settledTrades.length / trades.length) * 100).toFixed(0)
    : '—';

  // Monthly volume mock (derived from trades)
  const monthlyData = [
    { month: 'Apr', volume: 12500000, deals: 42 },
    { month: 'May', volume: 15800000, deals: 55 },
    { month: 'Jun', volume: 18200000, deals: 63 },
    { month: 'Jul', volume: 14900000, deals: 48 },
    { month: 'Aug', volume: 21300000, deals: 72 },
    { month: 'Sep', volume: 19400000, deals: 68 },
  ];

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const result = await onExportRegulatory();
      // Create a download
      const blob = new Blob([result.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `regulatory-extract-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    }
    setExporting(false);
  }, [onExportRegulatory]);

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white border border-zinc-200 rounded-none p-4">
          <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-1">Win Rate</p>
          <p className="text-2xl font-bold text-black">
            {quotes.length > 0 ? `${((wonQuotes.length / quotes.length) * 100).toFixed(0)}%` : '—'}
          </p>
          <p className="text-[10px] text-zinc-500">{wonQuotes.length} won / {quotes.length} total</p>
        </div>
        <div className="bg-white border border-zinc-200 rounded-none p-4">
          <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-1">Avg Win Spread</p>
          <p className="text-2xl font-bold text-black">{avgWinSpread.toFixed(4)}</p>
          <p className="text-[10px] text-zinc-500">Rate points</p>
        </div>
        <div className="bg-white border border-zinc-200 rounded-none p-4">
          <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-1">Total Volume</p>
          <p className="text-2xl font-bold text-black">{formatCurrency(trades.reduce((s, t) => s + t.amount, 0))}</p>
          <p className="text-[10px] text-zinc-500">{trades.length} trades</p>
        </div>
        <div className="bg-white border border-zinc-200 rounded-none p-4">
          <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-1">Settlement Rate</p>
          <p className="text-2xl font-bold text-black">{settlementRate}%</p>
          <p className="text-[10px] text-zinc-500">{settledTrades.length} settled, {failedTrades.length} failed</p>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Volume Trends */}
        <div className="bg-white border border-zinc-200 rounded-none p-6">
          <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 mb-4">Volume Trends</h3>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} stroke="#a1a1aa" />
                <YAxis tick={{ fontSize: 10 }} stroke="#a1a1aa" tickFormatter={(v) => formatCurrency(v)} />
                <Tooltip formatter={(v, name) => [name === 'volume' ? formatCurrency(v) : v, name === 'volume' ? 'Volume' : 'Deals']} contentStyle={{ border: '1px solid #e4e4e7', borderRadius: '0', fontSize: '11px' }} />
                <Line type="monotone" dataKey="volume" stroke="#52525b" strokeWidth={2} dot={{ r: 3, fill: '#52525b' }} />
                <Line type="monotone" dataKey="deals" stroke="#a1a1aa" strokeWidth={2} dot={{ r: 3, fill: '#a1a1aa' }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Quote Breakdown Pie */}
        <div className="bg-white border border-zinc-200 rounded-none p-6">
          <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 mb-4">Quote Outcomes</h3>
          <div className="h-52 flex items-center justify-center">
            {quoteBreakdown.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={quoteBreakdown} cx="50%" cy="50%" innerRadius={40} outerRadius={80} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                    {quoteBreakdown.map((_, idx) => (
                      <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ border: '1px solid #e4e4e7', borderRadius: '0', fontSize: '11px' }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-xs text-zinc-400">No quote data</p>
            )}
          </div>
        </div>
      </div>

      {/* Volume by Pair */}
      <div className="bg-white border border-zinc-200 rounded-none p-6">
        <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 mb-4">Volume by Currency Pair</h3>
        <div className="h-48">
          {pairVolume.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={pairVolume}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                <XAxis dataKey="pair" tick={{ fontSize: 10 }} stroke="#a1a1aa" />
                <YAxis tick={{ fontSize: 10 }} stroke="#a1a1aa" tickFormatter={(v) => formatCurrency(v)} />
                <Tooltip formatter={(v) => [formatCurrency(v), 'Volume']} contentStyle={{ border: '1px solid #e4e4e7', borderRadius: '0', fontSize: '11px' }} />
                <Bar dataKey="volume" fill="#52525b" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-full text-zinc-400 text-xs">
              <Activity className="w-6 h-6 mr-2" /> No trade data
            </div>
          )}
        </div>
      </div>

      {/* Spread Comparison */}
      <div className="bg-white border border-zinc-200 rounded-none p-6">
        <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 mb-4">Spread Comparison</h3>
        <div className="grid grid-cols-4 gap-4">
          <div>
            <p className="font-mono text-[10px] text-zinc-400 mb-1">Avg Win Spread</p>
            <p className="text-xl font-bold text-black">{avgWinSpread.toFixed(4)}</p>
          </div>
          <div>
            <p className="font-mono text-[10px] text-zinc-400 mb-1">Avg Loss Spread</p>
            <p className="text-xl font-bold text-black">{avgLossSpread.toFixed(4)}</p>
          </div>
          {competitiveContext && (
            <>
              <div>
                <p className="font-mono text-[10px] text-zinc-400 mb-1">Market Win Margin</p>
                <p className="text-xl font-bold text-black">{(competitiveContext.avgWinMargin * 100).toFixed(2)}%</p>
              </div>
              <div>
                <p className="font-mono text-[10px] text-zinc-400 mb-1">Market Win Rate</p>
                <p className="text-xl font-bold text-black">{(competitiveContext.winRate * 100).toFixed(0)}%</p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Regulatory Export */}
      <div className="bg-white border border-zinc-200 rounded-none p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500">Regulatory Extract</h3>
            <p className="text-xs text-zinc-400 mt-1">Export all trades and quotes as CSV for regulatory reporting</p>
          </div>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="flex items-center gap-2 px-4 py-2 bg-zinc-700 text-white text-xs font-mono uppercase tracking-wider hover:bg-zinc-800 disabled:opacity-50 rounded-none transition-colors"
          >
            <Download className="w-4 h-4" />
            {exporting ? 'Exporting...' : 'Export CSV'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsTab;
